/**
 * sl-hle4.12: Per-custom-app AI/LLM provenance + crawler-policy settings.
 *
 * Renders AIEO controls for one custom app at a time:
 *   - Crawler policy (ALLOW_ALL / BLOCK_ALL / PER_BOT) with per-bot
 *     allow/disallow toggles when PER_BOT is selected
 *   - EU AI Act Art. 50 declaration (human-authored / ai-assisted /
 *     ai-generated / mixed / undeclared) with conservative default
 *   - Training-data consent flag (default OFF — opt-in only)
 *   - Attribution URL for AI-rendered citations
 *   - llms.txt body override (≤10KB, with reset-to-auto)
 *
 * View selector matches the SEO section pattern (only shown when N>1).
 *
 * SOLID:
 *   - S: One section, AI provenance + crawler policy only.
 *   - D: Depends on GraphQLService + WorkspaceService — never touches the
 *     llm-crawler-policy service directly. Bot registry is fetched via
 *     the `knownAICrawlerBots` query and cached for the session.
 */
import {
  Button,
  Input,
  Menu,
  MenuItem,
  notify,
  RadioGroup,
} from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import { GraphQLService } from '@affine/core/modules/cloud';
import { GuardService } from '@affine/core/modules/permissions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import {
  customAppAieoQuery,
  customAppsQuery,
  knownAiCrawlerBotsQuery,
  updateCustomAppAieoMutation,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  type AICrawlerBot,
  type AieoDeclaration,
  type AieoSettings,
  buildRobotsPreview,
  buildUpdateCustomAppAieoInput,
  type CrawlerPolicy,
  normaliseCrawlerPolicyForDraft,
} from './aieo-policy-utils';

const LLMS_TEXT_LIMIT_BYTES = 10 * 1024;

type Declaration = AieoDeclaration;

const CRAWLER_POLICY_OPTIONS: CrawlerPolicy[] = [
  'ALLOW_ALL',
  'BLOCK_ALL',
  'PER_BOT',
];
const DECLARATION_OPTIONS: Declaration[] = [
  'undeclared',
  'human-authored',
  'ai-assisted',
  'ai-generated',
  'mixed',
];

interface ViewListItem {
  id: string;
  title: string | null;
  slug: string;
  isPublished: boolean;
}

const EMPTY_SETTINGS: AieoSettings = {
  crawlerPolicy: null,
  perBot: {},
  declaration: 'undeclared',
  trainingDataConsent: false,
  attributionUrl: '',
  llmsTextOverride: '',
};

function settingsEqual(a: AieoSettings, b: AieoSettings): boolean {
  return (
    a.crawlerPolicy === b.crawlerPolicy &&
    JSON.stringify(a.perBot) === JSON.stringify(b.perBot) &&
    a.declaration === b.declaration &&
    a.trainingDataConsent === b.trainingDataConsent &&
    a.attributionUrl === b.attributionUrl &&
    a.llmsTextOverride === b.llmsTextOverride
  );
}

function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

export const AieoSection: FC = () => {
  const t = useI18n();
  const graphqlService = useService(GraphQLService);
  const workspaceService = useService(WorkspaceService);
  const guardService = useService(GuardService);
  const workspaceId = workspaceService.workspace.id;
  const information = useWorkspaceInfo(workspaceService.workspace);
  const canEditCustomApps = useLiveData(
    guardService.can$('Workspace_CustomApps_Edit')
  );
  const isAdmin = !!(
    information?.isOwner ||
    information?.isAdmin ||
    canEditCustomApps === true
  );

  const [views, setViews] = useState<ViewListItem[] | null>(null);
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const [bots, setBots] = useState<AICrawlerBot[]>([]);
  const [original, setOriginal] = useState<AieoSettings | null>(null);
  const [draft, setDraft] = useState<AieoSettings | null>(null);
  const [viewsError, setViewsError] = useState<string | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [isLoadingViews, setIsLoadingViews] = useState(true);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadInitial = useAsyncCallback(async () => {
    setIsLoadingViews(true);
    setViewsError(null);
    try {
      const [viewsRes, botsRes] = await Promise.all([
        graphqlService.gql({
          query: customAppsQuery,
          variables: { workspaceId },
        }),
        graphqlService.gql({
          query: knownAiCrawlerBotsQuery,
        }),
      ]);
      const list = viewsRes.customApps
        .filter(v => v.isPublished)
        .map(v => ({
          id: v.id,
          title: v.title,
          slug: v.slug,
          isPublished: v.isPublished,
        }));
      setViews(list);
      setBots(botsRes.knownAICrawlerBots);
      if (list.length > 0 && !selectedViewId) {
        setSelectedViewId(list[0].id);
      } else if (
        list.length > 0 &&
        selectedViewId &&
        !list.some(v => v.id === selectedViewId)
      ) {
        setSelectedViewId(list[0].id);
      } else if (list.length === 0) {
        setSelectedViewId(null);
      }
    } catch (err) {
      const friendly = UserFriendlyError.fromAny(err);
      setViewsError(friendly.message ?? 'Failed to load AIEO data');
      setViews([]);
    } finally {
      setIsLoadingViews(false);
    }
  }, [graphqlService, workspaceId, selectedViewId]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const loadSettings = useAsyncCallback(
    async (customAppId: string) => {
      setIsLoadingSettings(true);
      setSettingsError(null);
      try {
        const result = await graphqlService.gql({
          query: customAppAieoQuery,
          variables: { customAppId, workspaceId },
        });
        const aieo = result.customAppAIEO;
        if (!aieo) {
          setOriginal({ ...EMPTY_SETTINGS });
          setDraft({ ...EMPTY_SETTINGS });
          return;
        }
        const policy = normaliseCrawlerPolicyForDraft(aieo.crawlerPolicy);
        const perBot = (aieo.crawlerPolicyDetails ?? {}) as Record<
          string,
          'ALLOW' | 'DISALLOW'
        >;
        const declaration: Declaration = isDeclaration(aieo.declaration)
          ? aieo.declaration
          : 'undeclared';
        const next: AieoSettings = {
          crawlerPolicy: policy,
          perBot: { ...perBot },
          declaration,
          trainingDataConsent: aieo.trainingDataConsent ?? false,
          attributionUrl: aieo.attributionUrl ?? '',
          llmsTextOverride: aieo.llmsTextOverride ?? '',
        };
        setOriginal(next);
        setDraft({ ...next, perBot: { ...next.perBot } });
      } catch (err) {
        const friendly = UserFriendlyError.fromAny(err);
        setSettingsError(friendly.message ?? 'Failed to load AIEO settings');
        setOriginal(null);
        setDraft(null);
      } finally {
        setIsLoadingSettings(false);
      }
    },
    [graphqlService, workspaceId]
  );

  useEffect(() => {
    if (selectedViewId) {
      loadSettings(selectedViewId);
    }
  }, [selectedViewId, loadSettings]);

  const dirty = useMemo(
    () => !!(original && draft && !settingsEqual(original, draft)),
    [original, draft]
  );

  const llmsByteLength = useMemo(
    () => (draft ? byteLength(draft.llmsTextOverride) : 0),
    [draft]
  );
  const llmsOverLimit = llmsByteLength > LLMS_TEXT_LIMIT_BYTES;

  const setDraftField = useCallback(
    <K extends keyof AieoSettings>(key: K, value: AieoSettings[K]) => {
      setDraft(prev => (prev ? { ...prev, [key]: value } : prev));
    },
    []
  );

  const toggleBot = useCallback((botId: string) => {
    setDraft(prev => {
      if (!prev) return prev;
      const next = { ...prev.perBot };
      const current = next[botId];
      next[botId] = current === 'DISALLOW' ? 'ALLOW' : 'DISALLOW';
      return { ...prev, perBot: next };
    });
  }, []);

  const resetLlmsText = useCallback(() => {
    setDraftField('llmsTextOverride', '');
  }, [setDraftField]);

  const handleSave = useAsyncCallback(async () => {
    if (!isAdmin || !draft || !selectedViewId) return;
    if (llmsOverLimit) {
      notify.error({
        title:
          t[
            'com.streamlain.settings.workspace.customApps.aieo.llms.overLimit'
          ](),
      });
      return;
    }
    setIsSaving(true);
    try {
      await graphqlService.gql({
        query: updateCustomAppAieoMutation,
        variables: {
          input: buildUpdateCustomAppAieoInput(
            draft,
            selectedViewId,
            workspaceId
          ),
        },
      });
      notify.success({
        title:
          t['com.streamlain.settings.workspace.customApps.aieo.savedToast'](),
      });
      loadSettings(selectedViewId);
    } catch (err) {
      notify.error({
        title:
          t['com.streamlain.settings.workspace.customApps.aieo.saveFailed'](),
        message: UserFriendlyError.fromAny(err).message ?? String(err),
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    isAdmin,
    draft,
    selectedViewId,
    workspaceId,
    graphqlService,
    llmsOverLimit,
    loadSettings,
    t,
  ]);

  const sectionTitle =
    t['com.streamlain.settings.workspace.customApps.aieo.title']();

  if (isLoadingViews) {
    return (
      <SettingWrapper title={sectionTitle}>
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.aieo.loading'
          ]()}
          desc=""
        />
      </SettingWrapper>
    );
  }

  if (viewsError) {
    return (
      <SettingWrapper title={sectionTitle}>
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.aieo.viewsError'
          ]()}
          desc={viewsError}
        />
      </SettingWrapper>
    );
  }

  if (!views || views.length === 0) {
    return (
      <SettingWrapper title={sectionTitle}>
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.aieo.empty.title'
          ]()}
          desc={t[
            'com.streamlain.settings.workspace.customApps.aieo.empty.desc'
          ]()}
        />
      </SettingWrapper>
    );
  }

  const selectedView = views.find(v => v.id === selectedViewId) ?? null;
  const showSelector = views.length > 1;
  const robotsPreview =
    draft && selectedView
      ? buildRobotsPreview(draft, bots, selectedView.slug)
      : '';

  return (
    <SettingWrapper title={sectionTitle}>
      {showSelector ? (
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.aieo.selector.label'
          ]()}
          desc={t[
            'com.streamlain.settings.workspace.customApps.aieo.selector.desc'
          ]()}
        >
          <Menu
            items={views.map(view => (
              <MenuItem
                key={view.id}
                onClick={() => setSelectedViewId(view.id)}
                selected={view.id === selectedViewId}
              >
                {view.title?.length ? view.title : view.slug}
              </MenuItem>
            ))}
          >
            <Button>
              {selectedView?.title?.length
                ? selectedView.title
                : (selectedView?.slug ?? '—')}
            </Button>
          </Menu>
        </SettingRow>
      ) : null}

      {isLoadingSettings ? (
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.aieo.loadingSettings'
          ]()}
          desc=""
        />
      ) : settingsError ? (
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.aieo.loadFailed'
          ]()}
          desc={settingsError}
        >
          <Button
            variant="secondary"
            onClick={() => selectedViewId && loadSettings(selectedViewId)}
          >
            {t['com.streamlain.settings.workspace.customApps.aieo.retry']()}
          </Button>
        </SettingRow>
      ) : !draft ? null : (
        <>
          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.aieo.policy.label'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.aieo.policy.desc'
            ]()}
            spreadCol={false}
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <PolicyChip
                label="Workspace default"
                checked={draft.crawlerPolicy === null}
                disabled={!isAdmin || isSaving}
                onClick={() => setDraftField('crawlerPolicy', null)}
              />
              {CRAWLER_POLICY_OPTIONS.map(option => (
                <PolicyChip
                  key={option}
                  label={
                    t[
                      `com.streamlain.settings.workspace.customApps.aieo.policy.${option}` as
                        | 'com.streamlain.settings.workspace.customApps.aieo.policy.ALLOW_ALL'
                        | 'com.streamlain.settings.workspace.customApps.aieo.policy.BLOCK_ALL'
                        | 'com.streamlain.settings.workspace.customApps.aieo.policy.PER_BOT'
                    ]?.() ?? option
                  }
                  checked={draft.crawlerPolicy === option}
                  disabled={!isAdmin || isSaving}
                  onClick={() => setDraftField('crawlerPolicy', option)}
                />
              ))}
            </div>
          </SettingRow>

          {draft.crawlerPolicy === 'PER_BOT' ? (
            <SettingRow
              name={t[
                'com.streamlain.settings.workspace.customApps.aieo.bots.label'
              ]()}
              desc={t[
                'com.streamlain.settings.workspace.customApps.aieo.bots.desc'
              ]()}
              spreadCol={false}
            >
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {bots.map(bot => {
                  const allowed = draft.perBot[bot.id] !== 'DISALLOW';
                  return (
                    <PolicyChip
                      key={bot.id}
                      label={allowed ? `${bot.label} ✓` : `${bot.label} ✗`}
                      checked={allowed}
                      disabled={!isAdmin || isSaving}
                      onClick={() => toggleBot(bot.id)}
                    />
                  );
                })}
              </div>
            </SettingRow>
          ) : null}

          <SettingRow name="robots.txt" desc="" spreadCol={false}>
            <textarea
              value={robotsPreview}
              readOnly
              rows={Math.min(8, Math.max(3, robotsPreview.split('\n').length))}
              style={{
                width: '100%',
                padding: '6px 10px',
                borderRadius: 6,
                border:
                  '1px solid var(--streamlain-v2-layer-insideBorder-border)',
                background: 'var(--streamlain-v2-layer-background-secondary)',
                color: 'var(--streamlain-v2-text-primary)',
                fontFamily: 'monospace',
                fontSize: 12,
                resize: 'vertical',
              }}
            />
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.aieo.declaration.label'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.aieo.declaration.desc'
            ]()}
          >
            <RadioGroup
              value={draft.declaration}
              onChange={value => setDraftField('declaration', value)}
              disabled={!isAdmin || isSaving}
              items={DECLARATION_OPTIONS.map(option => ({
                value: option,
                label:
                  t[
                    `com.streamlain.settings.workspace.customApps.aieo.declaration.option.${option}` as
                      | 'com.streamlain.settings.workspace.customApps.aieo.declaration.option.undeclared'
                      | 'com.streamlain.settings.workspace.customApps.aieo.declaration.option.human-authored'
                      | 'com.streamlain.settings.workspace.customApps.aieo.declaration.option.ai-assisted'
                      | 'com.streamlain.settings.workspace.customApps.aieo.declaration.option.ai-generated'
                      | 'com.streamlain.settings.workspace.customApps.aieo.declaration.option.mixed'
                  ]?.() ?? option,
              }))}
            />
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.aieo.consent.label'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.aieo.consent.desc'
            ]()}
          >
            <Button
              variant={draft.trainingDataConsent ? 'primary' : 'secondary'}
              disabled={!isAdmin || isSaving}
              onClick={() =>
                setDraftField('trainingDataConsent', !draft.trainingDataConsent)
              }
            >
              {draft.trainingDataConsent
                ? t[
                    'com.streamlain.settings.workspace.customApps.aieo.consent.on'
                  ]()
                : t[
                    'com.streamlain.settings.workspace.customApps.aieo.consent.off'
                  ]()}
            </Button>
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.aieo.attribution.label'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.aieo.attribution.desc'
            ]()}
          >
            <Input
              value={draft.attributionUrl}
              onChange={value => setDraftField('attributionUrl', value)}
              disabled={!isAdmin || isSaving}
              placeholder="https://example.com/about"
              style={{ minWidth: 280 }}
            />
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.aieo.llms.label'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.aieo.llms.desc'
            ]()}
            spreadCol={false}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                width: '100%',
              }}
            >
              <textarea
                value={draft.llmsTextOverride}
                onChange={e =>
                  setDraftField('llmsTextOverride', e.target.value)
                }
                disabled={!isAdmin || isSaving}
                rows={6}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: llmsOverLimit
                    ? '1px solid var(--streamlain-error-color)'
                    : '1px solid var(--streamlain-v2-layer-insideBorder-border)',
                  background: 'var(--streamlain-v2-layer-background-primary)',
                  color: 'var(--streamlain-v2-text-primary)',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  resize: 'vertical',
                }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: llmsOverLimit
                    ? 'var(--streamlain-error-color)'
                    : 'var(--streamlain-v2-text-tertiary)',
                }}
              >
                <span>
                  {llmsByteLength} / {LLMS_TEXT_LIMIT_BYTES} bytes
                </span>
                <Button
                  variant="plain"
                  disabled={
                    !isAdmin || isSaving || draft.llmsTextOverride.length === 0
                  }
                  onClick={resetLlmsText}
                >
                  {t[
                    'com.streamlain.settings.workspace.customApps.aieo.llms.reset'
                  ]()}
                </Button>
              </div>
            </div>
          </SettingRow>

          {isAdmin ? (
            <SettingRow name="" desc="">
              <Button
                variant="primary"
                disabled={!dirty || isSaving || llmsOverLimit}
                onClick={handleSave}
              >
                {isSaving
                  ? t[
                      'com.streamlain.settings.workspace.customApps.aieo.savingAction'
                    ]()
                  : t[
                      'com.streamlain.settings.workspace.customApps.aieo.saveAction'
                    ]()}
              </Button>
            </SettingRow>
          ) : null}
        </>
      )}
    </SettingWrapper>
  );
};

interface PolicyChipProps {
  label: string;
  checked: boolean;
  disabled: boolean;
  onClick: () => void;
}

const PolicyChip: FC<PolicyChipProps> = ({
  label,
  checked,
  disabled,
  onClick,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-checked={checked || undefined}
      style={{
        padding: '4px 10px',
        borderRadius: 16,
        border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
        background: checked
          ? 'var(--streamlain-v2-layer-background-primary)'
          : 'var(--streamlain-v2-layer-background-secondary)',
        color: checked
          ? 'var(--streamlain-v2-text-primary)'
          : 'var(--streamlain-v2-text-secondary)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {label}
    </button>
  );
};

function isDeclaration(value: string | null | undefined): value is Declaration {
  return (
    value === 'human-authored' ||
    value === 'ai-assisted' ||
    value === 'ai-generated' ||
    value === 'mixed' ||
    value === 'undeclared'
  );
}
